/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import React from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import type { HostContext, Logger, RootState } from '../../../@types';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import ClickBase from '../../../containers/SearchResultsPage/ClickBase';
import FileTypeService from '../../../services/FileTypeService';
import {
	setExactTypedSearchQuery,
	setSearchQuery,
	setSourceOfQuery,
	setSubmittedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
} from '../../../actions/searchActions';
import SearchService from '../../../services/SearchService';
import SearchUtils from '../../../utils/SearchUtils';
import { SourceOfQuery } from '../../../containers/SearchResultsPage/SearchConstants';
import SearchHostContext from '../../../utils/SearchHostContext';

type OwnProps = {
	data: Record<string, any>;
	index: number;
	count: number;
	lastCard?: boolean;
	searchHostContext: HostContext;
	logger: Logger;
	onSubmit: (...args: Array<any>) => any;
	searchQuery: string;
	hostShowSearchDropdown: boolean;
	tabIndex?: boolean;
};

type SearchDropDownSummaryItemProps = OwnProps & WrappedComponentProps & PropsFromRedux;

class SearchDropDownSummaryItem extends ClickBase<SearchDropDownSummaryItemProps> {
	private analytics: SearchAnalytics;
	quickActionTranslation: string | Record<string, any>;

	constructor(props: SearchDropDownSummaryItemProps) {
		super(props);
		this.quickActionTranslation = FileTypeService.fileTypeDataObject?.translations?.preSearchQuickActions || {};
		this.analytics = new SearchAnalytics(this.props.searchHostContext);
	}

	getTitleId(): string {
		const localeKey = `app.search.presearch.quickActions.${this.props.data?.id}`;
		const titleId = this.quickActionTranslation?.[localeKey];
		return titleId || localeKey;
	}

	postAnalyticsEvent(eventType: string) {
		SearchAnalytics.keyword = this.props.data?.name;
		this.analytics.postPreSearchItemAction({
			eventType,
			data: [this.props.data],
			recommendationType: 'Leo Recommended Summaries',
			uiSortOrder: 0,
			eventIndex: this.props.index,
			sendUISearchKeyword: true,
		});
	}

	keyDownHandler = (event: Record<string, any>) => {
		if (
			event.keyCode === 40 ||
			event.key === 'ArrowDown' ||
			event.keyCode === 38 ||
			event.key === 'ArrowUp' ||
			event.key === 'Tab' ||
			event.which === 9
		) {
			event.stopPropagation();
			event.preventDefault();
		}
	};

	/*
	 * Executes the Search Onboarding Suggestion Item Search Query
	 */
	async executeQuery(index: number, item: string, exactTypedSearchQuery: string) {
		this.props.setSourceOfQuery(SourceOfQuery.LeoRecommendation);
		this.postAnalyticsEvent('click');
		const trimmedSearchQuery = item.trim();
		SearchAnalytics.searchType = SearchAnalytics.autoComplete;
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		analytics.initSearchAnalytics(trimmedSearchQuery);
		analytics.postDropDownSelection(index, exactTypedSearchQuery);
		this.props.setTempSearchQuery('');
		this.props.setSearchQuery(trimmedSearchQuery);
		this.props.setExactTypedSearchQuery(exactTypedSearchQuery);
		this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
		this.props.setSubmittedSearchQuery(true);
		// Save to local storage in case the refresh the results page
		this.props.onSubmit(trimmedSearchQuery);
		this.props.logger.info('Search Auto Suggestion', 'Logged Search Query', trimmedSearchQuery);
		SearchUtils.setSearchHistoryWithSearchQuery(this.props.searchHistoryItems, trimmedSearchQuery);

		try {
			const authenticated = SearchHostContext.isAuthenticated();
			if (
				authenticated &&
				this.props.searchHostContext.featureFlags.includes('temp_enable_search_history_server') &&
				trimmedSearchQuery !== ''
			) {
				const searchService = new SearchService(this.props.searchHostContext);
				await searchService.pushSearchHistory(trimmedSearchQuery);
			}
		} catch (e) {
			this.props.logger.error(
				'Search Auto Suggestion Item',
				'Problem creating/updating search history to search server on search auto suggestion click',
				e,
			);
		}
	}

	render() {
		const { data, index } = this.props;
		return (
			<div
				id={`search-dropdown-gen-ai-summary-item-${index}`}
				className={`search-dropdown-gen-ai-summary-item gen-summary ${this.props.lastCard ? ' last-item' : ''}`}
				onClick={() => this.executeQuery(index, data?.name, this.props.searchQuery)}
				tabIndex={this.props.tabIndex ? 0 : undefined}
				onKeyDown={this.keyDownHandler}
			>
				<span className='search-dropdown-gen-ai-summary-item-text-title-container gen-summary-item'>
					{data?.name}
				</span>
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	theme: search.theme,
	searchHistoryItems: search.searchHistoryItems,
	searchQueryValue: search.searchQuery,
});

const mapDispatchToProps = {
	setSubmittedSearchQuery,
	setSearchQuery,
	setExactTypedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
	setSourceOfQuery,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropDownSummaryItem));
